/**
 * Change detection for the QuikFlow canvas.
 *
 * The workflow used to track "are there unsaved changes?" with a hand-set boolean that three
 * event handlers flipped to true (sidebar submit, node drag stop, connect start). Events are a
 * bad proxy for change: React Flow fires a drag on a plain click, closing a step sidebar
 * force-submits its form even when nothing was typed, and an abandoned connect drag still fires
 * onConnectStart. The result was a workflow that reported "unsaved changes" after you merely
 * looked at a step.
 *
 * These helpers compare *data* instead of listening to events. Every step is hashed on its own,
 * so we can also send only the steps that actually changed to `PUT /flow/team/:uid` — the
 * endpoint iterates whatever `steps` array it is given and never deletes the ones you omit.
 */

/**
 * JSON.stringify with sorted object keys.
 *
 * Plain JSON.stringify emits keys in insertion order, so `{a:1,b:2}` and `{b:2,a:1}` serialize
 * differently despite being equal. Object spreads all over this page reorder keys constantly,
 * which would show up as phantom changes. Sorting makes the output canonical.
 */
export const stableStringify = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

/**
 * Canonical form of diagramData for comparison purposes only — never send this to the API,
 * always send the real array.
 *
 * Two things in diagramData are not user edits:
 *  - `position` holds floats. React Flow re-computes them on zoom/fit/resize, so x can drift
 *    from 120 to 120.00000000000001 without anyone touching the canvas. Rounded to whole pixels.
 *  - `measured` is the node's rendered width/height, written by React Flow after first paint and
 *    again on any re-measure. It describes the DOM, not the workflow. Dropped.
 */
export const normalizeDiagramForCompare = (diagramData: any[]): any[] =>
  (diagramData || []).map(({ measured, ...rest }: any) => ({
    ...rest,
    position: {
      x: Math.round(rest?.position?.x ?? 0),
      y: Math.round(rest?.position?.y ?? 0)
    }
  }));

/** Hash of the whole layout. One string; layout is saved all-or-nothing. */
export const hashDiagram = (diagramData: any[]): string =>
  stableStringify(normalizeDiagramForCompare(diagramData || []));

/**
 * One hash per step, keyed by step uid.
 *
 * Per-step rather than one big hash so a save can carry only the steps that moved. useBuildNodeTree
 * strips `node` (and therefore `position`) off each step before returning it, so dragging a node
 * never changes any step hash — layout and content are already cleanly separated upstream.
 */
export const hashSteps = (steps: any[]): Map<string, string> => {
  const hashes = new Map<string, string>();
  (steps || []).forEach((step: any) => {
    if (!step?.uid) return;
    hashes.set(step.uid, stableStringify(step));
  });
  return hashes;
};

/** Steps whose hash differs from the last successfully-saved baseline (new steps included). */
export const getChangedSteps = (steps: any[], baseline: Map<string, string>, current: Map<string, string>): any[] =>
  (steps || []).filter((step: any) => step?.uid && baseline.get(step.uid) !== current.get(step.uid));
